/**
 * Analytics Tracker Aggregator
 *
 * Combines all event loggers into a single unified API for analytics tracking.
 * Provides namespaced access to different categories of events, plus
 * identity methods for user identification and group analytics.
 * @module features/analytics/utils/analyticsTracker
 */
import { coreEventLogger } from "./coreEventLogger";
import { noteEventLogger } from "./noteEventLogger";
import { notificationEventLogger } from "./notificationEventLogger";
import { pipelineEventLogger } from "./pipelineEventLogger";
import { playerEventLogger } from "./playerEventLogger";
import {
  captureException,
  clearAnalyticsOrganizationContext,
  identifyUser,
  setAnalyticsGroup,
  setAnalyticsOrganizationContext,
  resetAnalytics,
} from "./posthogSender";
import { profilerEventLogger } from "./profilerEventLogger";
import { scoutingEventLogger } from "./scoutingEventLogger";
import { searchEventLogger } from "./searchEventLogger";
import { surfaceEventLogger } from "./surfaceEventLogger";
import { userEventLogger } from "./userEventLogger";

/**
 * Main analytics tracker object providing type-safe event logging
 * for PostHog Analytics on web, iOS, and Android platforms.
 *
 * Core events (like screen view) are available at the top level.
 * Other events are namespaced by category.
 * Identity methods enable user/group tracking across sessions.
 * @example
 * ```typescript
 * import { AnalyticsTracker } from '@/features/analytics';
 *
 * // Log a screen view (core event - top level)
 * AnalyticsTracker.logScreenView({
 *   screen_name: "Home",
 *   screen_class: "HomeScreen"
 * });
 *
 * // Identify user after sign-in
 * AnalyticsTracker.identifyUser("user-123", { phone_number: "+1234567890" });
 *
 * // Associate user with organization
 * AnalyticsTracker.setGroup("organization", "org-456", { name: "Acme FC" });
 *
 * // Reset identity on sign-out
 * AnalyticsTracker.resetIdentity();
 * ```
 */
export const AnalyticsTracker = {
  /** Core event: Log screen view directly */
  ...coreEventLogger,

  /** Note-related analytics events */
  note: noteEventLogger,

  /** Push-notification engagement analytics events (SE-4010) */
  notification: notificationEventLogger,

  /** Player-centric feature analytics events (SE-4010) */
  player: playerEventLogger,

  /** Recruitment-pipeline analytics events (SE-5508) */
  pipeline: pipelineEventLogger,

  /** React Profiler performance analytics events */
  profiler: profilerEventLogger,

  /** Scouting workflow analytics events (SE-4010) */
  scouting: scoutingEventLogger,

  /** Player discovery (search / filter) analytics events (SE-4010) */
  search: searchEventLogger,

  /** Engagement-surface view analytics events (SE-4010) */
  surface: surfaceEventLogger,

  /** User lifecycle and activity analytics events */
  user: userEventLogger,

  /** Forwards a caught exception to PostHog as a `$exception` event (Sentry stays SoT) */
  captureException,

  /** Identifies a user in PostHog for cross-session tracking */
  identifyUser,

  /** Associates the current user with a group (e.g., organization) */
  setGroup: setAnalyticsGroup,

  /** Registers `org_id` / `org_name` super-properties for the resolved org (SE-4009) */
  setOrganizationContext: setAnalyticsOrganizationContext,

  /** Clears the `org_id` / `org_name` super-properties (SE-4009) */
  clearOrganizationContext: clearAnalyticsOrganizationContext,

  /** Resets PostHog identity on sign-out */
  resetIdentity: resetAnalytics,
};
